import { endOfDay, startOfDay } from 'date-fns';
import {
  defaultPlaybookTrackerEnvs,
  PlaybookTrackerEsResponse,
  PlaybookTrackerFilterOptions,
  PlaybookTrackerRow,
  PlaybookTrackerSearchFilters,
} from './types';

const getUniqueValues = (
  rows: PlaybookTrackerRow[],
  key: keyof PlaybookTrackerRow,
): string[] =>
  Array.from(
    new Set(
      rows
        .map((row) => row[key])
        .filter(
          (value): value is string => typeof value === 'string' && !!value,
        ),
    ),
  ).sort((first, second) => first.localeCompare(second));

const formatTimestamp = (value?: number) => {
  if (!value) return '-';

  const resolvedDate = new Date(value);

  if (Number.isNaN(resolvedDate.getTime())) return String(value);

  return resolvedDate.toLocaleString();
};

export const buildPlaybookTrackerPayload = (
  filters: PlaybookTrackerSearchFilters,
  from: number = 0, // 👈 ADD
) => {
  const must: Array<Record<string, unknown>> = [];
  const filter: Array<Record<string, unknown>> = [];

  if (filters.clientId) {
    must.push({ term: { clientId: filters.clientId } });
  }

  if (filters.recordId) {
    must.push({ term: { recordId: filters.recordId } });
  }

  if (filters.playbookCode) {
    must.push({ term: { playbookCode: filters.playbookCode } });
  }

  if (filters.status) {
    must.push({ term: { status: filters.status } });
  }

  if (filters.startDate || filters.endDate) {
    const range: Record<string, number> = {};

    if (filters.startDate) {
      range.gte = startOfDay(filters.startDate).getTime();
    }

    if (filters.endDate) {
      range.lte = endOfDay(filters.endDate).getTime();
    }

    filter.push({
      range: {
        createdAt: range,
      },
    });
  }

  return {
    from,
    size: 50,
    sort: [{ createdAt: { order: 'desc' } }],
    query: {
      bool: {
        ...(must.length ? { must } : {}),
        ...(filter.length ? { filter } : {}),
        must_not: [
          {
            term: {
              deleted: true,
            },
          },
        ],
      },
    },
  };
};

export const transformPlaybookTrackerResponse = (
  response: PlaybookTrackerEsResponse,
): PlaybookTrackerRow[] =>
  (response?.hits?.hits || []).map((item) => {
    const {
      _source: source = {},
      _id: id,
      _index: index,
      _type: type,
      _score: score,
    } = item;

    return {
      ...source,
      _id: id,
      _index: index,
      _type: type,
      _score: score,
      createdAtLabel: formatTimestamp(source.createdAt),
      updatedAtLabel: formatTimestamp(source.updatedAt),
    };
  });

export const buildPlaybookTrackerFilters = (
  rows: PlaybookTrackerRow[],
): PlaybookTrackerFilterOptions => ({
  envs: Array.from(
    new Set([...defaultPlaybookTrackerEnvs, ...getUniqueValues(rows, 'env')]),
  ),
  clients: getUniqueValues(rows, 'clientId'),
  leadIds: getUniqueValues(rows, 'recordId'),
  playbookCodes: getUniqueValues(rows, 'playbookCode'),
  statuses: Array.from(new Set([...getUniqueValues(rows, 'status')])),
});

export const formatTrackerPayload = (value: unknown): string => {
  if (typeof value === 'string') {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return value;
    }

    try {
      return JSON.stringify(JSON.parse(trimmedValue), null, 2);
    } catch {
      return value;
    }
  }

  if (typeof value === 'object' && value !== null) {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }

  return String(value);
};

export const getDisplayValue = (
  value: PlaybookTrackerRow[keyof PlaybookTrackerRow],
) => {
  if (value === null || typeof value === 'undefined' || value === '') {
    return '-';
  }

  return String(value);
};
